import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

// Segment flex proportions:
// Well Stocked = 1/3, remainder split by threshold ranges
const WELL_FLEX = 10 / 5;
const RISK_FLEX = 10 - WELL_FLEX; // ≈ 8
const MIN_HIGH_FLEX = 1.2;     // minimum flex for "High Risk" text
const MIN_MODERATE_FLEX = 1.6; // minimum flex for "Moderate Risk" text (longer label)

export interface HealthStatusDisplay {
  header: string;
  shortHeader?: string;
  range: string;
  startPoint: string;
  body: string;
  action: string;
}

@Component({
  selector: 'app-product-ad-inventory-health',
  templateUrl: './product-ad-inventory-health.component.html',
  styleUrls: ['./product-ad-inventory-health.component.scss'],
})
export class ProductAdInventoryHealthComponent implements OnChanges {
  @Input() daysUntilStockOut: number = 0;
  @Input() minThreshold: number = 30;
  @Input() maxThreshold: number = 90;

  riskClass: string = 'risk-oos';
  indicatorLeft: string = '0%';

  statusDisplay: Record<string, HealthStatusDisplay> = {};
  segmentFlex: Record<string, number> = {};

  readonly statusSegments = [
    { class: 'seg-orange', state: 'HIGH_RISK' },
    { class: 'seg-yellow', state: 'MODERATE_RISK' },
    { class: 'seg-green', state: 'WELL_STOCK' },
  ];

  get currentStatusKey(): string {
    if (this.daysUntilStockOut === 0) return 'OUT_OF_STOCK';
    if (this.daysUntilStockOut < this.minThreshold) return 'HIGH_RISK';
    if (this.daysUntilStockOut >= this.minThreshold && this.daysUntilStockOut <= this.maxThreshold) return 'MODERATE_RISK';
    return 'WELL_STOCK';
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.buildStatusDisplay();
    this.buildSegmentFlex();
    this.calculateRisk();
  }

  private buildStatusDisplay(): void {
    this.statusDisplay = {
      OUT_OF_STOCK: {
        header: 'Out of Stock',
        shortHeader: 'OOS',
        range: '0 DAYS',
        startPoint: '0',
        body: 'Item currently has zero days of inventory supply available.',
        action: 'Restock immediately',
      },
      HIGH_RISK: {
        header: 'High Risk',
        range: `1 - ${this.minThreshold}`,
        startPoint: '1',
        body: 'Item is at a high risk of stocking out soon.',
        action: 'Pause campaigns or restock',
      },
      MODERATE_RISK: {
        header: 'Moderate Risk',
        range: `${this.minThreshold} - ${this.maxThreshold}`,
        startPoint: `${this.minThreshold}`,
        body: 'Item has moderate supply remaining.',
        action: 'Monitor inventory levels',
      },
      WELL_STOCK: {
        header: 'Well Stocked',
        range: `${this.maxThreshold}+`,
        startPoint: `${this.maxThreshold}+`,
        body: 'Item has plentiful stock to satisfy standard ad velocity.',
        action: 'Maintain campaigns',
      },
    };
  }

  private buildSegmentFlex(): void {
    const highRange = this.minThreshold;
    const moderateRange = this.maxThreshold - this.minThreshold;
    const totalRange = this.maxThreshold;

    let highFlex = RISK_FLEX * (highRange / totalRange);
    let modFlex = RISK_FLEX * (moderateRange / totalRange);

    // Clamp: ensure both risk segments have enough width for their text
    if (highFlex < MIN_HIGH_FLEX) {
      highFlex = MIN_HIGH_FLEX;
      modFlex = RISK_FLEX - highFlex;
    } else if (modFlex < MIN_MODERATE_FLEX) {
      modFlex = MIN_MODERATE_FLEX;
      highFlex = RISK_FLEX - modFlex;
    }

    this.segmentFlex = {
      HIGH_RISK: highFlex,
      MODERATE_RISK: modFlex,
      WELL_STOCK: WELL_FLEX,
    };
  }

  private calculateRisk(): void {
    const highWidth = this.segmentFlex['HIGH_RISK'];
    const modWidth = this.segmentFlex['MODERATE_RISK'];
    const wellWidth = this.segmentFlex['WELL_STOCK'];
    const totalFlex = highWidth + modWidth + wellWidth;

    const highPct = (highWidth / totalFlex) * 100;
    const modPct = (modWidth / totalFlex) * 100;
    const wellPct = (wellWidth / totalFlex) * 100;

    if (this.daysUntilStockOut === 0) {
      this.riskClass = 'risk-oos';
      this.indicatorLeft = '0%';
    } else if (this.daysUntilStockOut <= this.minThreshold) {
      this.riskClass = 'risk-high';
      const progress = this.daysUntilStockOut / this.minThreshold;
      this.indicatorLeft = `${progress * highPct}%`;
    } else if (this.daysUntilStockOut <= this.maxThreshold) {
      this.riskClass = 'risk-moderate';
      const progress = (this.daysUntilStockOut - this.minThreshold) / (this.maxThreshold - this.minThreshold);
      this.indicatorLeft = `${highPct + progress * modPct}%`;
    } else {
      this.riskClass = 'risk-well';
      const maxDisplay = this.maxThreshold * 2;
      const progress = Math.min((this.daysUntilStockOut - this.maxThreshold) / (maxDisplay - this.maxThreshold), 1);
      this.indicatorLeft = `${highPct + modPct + progress * wellPct}%`;
    }
  }
}
